import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  FolderOpen,
  Play,
  Trash2,
  Square,
  Loader2,
  Code2,
  Circle,
  LogOut,
} from 'lucide-react';
import { useWorkspaceStore } from '@/store';
import { cn } from '@/lib/utils';
import type { Workspace } from '@/types';

const statusColors: Record<Workspace['status'], string> = {
  creating: 'text-blue-400 fill-blue-400',
  running: 'text-green-400 fill-green-400',
  stopped: 'text-[#858585] fill-[#858585]',
  error: 'text-red-400 fill-red-400',
};

export default function ProjectsPage() {
  const navigate = useNavigate();
  const {
    workspaces,
    isLoading,
    fetchWorkspaces,
    selectWorkspace,
    startWorkspace,
    stopWorkspace,
    deleteWorkspace,
  } = useWorkspaceStore();

  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  const handleOpen = (ws: Workspace) => {
    selectWorkspace(ws);
    navigate(`/ide/${ws.id}`);
  };

  const handleDelete = async (id: string) => {
    await deleteWorkspace(id);
    setDeleteConfirm(null);
  };

  return (
    <div className="min-h-screen bg-[#1e1e1e] text-white">
      {/* Header */}
      <header className="border-b border-[#333] bg-[#252526]">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Code2 className="w-7 h-7 text-[#0e639c]" />
            <h1 className="text-xl font-semibold">Kubernodes IDE</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/templates')}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0e639c] hover:bg-[#1177bb] text-white rounded text-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              New Workspace
            </button>
            <button
              onClick={() => {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                navigate('/login');
              }}
              className="inline-flex items-center gap-2 px-3 py-2 bg-[#3c3c3c] hover:bg-[#505050] text-[#858585] hover:text-white rounded text-sm transition-colors"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        <h2 className="text-lg font-medium text-[#cccccc] mb-6">
          Your Workspaces
        </h2>

        {isLoading && workspaces.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#858585] animate-spin" />
          </div>
        ) : workspaces.length === 0 ? (
          <div className="text-center py-20">
            <FolderOpen className="w-16 h-16 text-[#858585] mx-auto mb-4" />
            <p className="text-[#858585] mb-6">
              No workspaces yet. Create one to get started.
            </p>
            <button
              onClick={() => navigate('/templates')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0e639c] hover:bg-[#1177bb] text-white rounded text-sm transition-colors"
            >
              <Plus className="w-4 h-4" />
              Create Workspace
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workspaces.map((ws) => (
              <div
                key={ws.id}
                className="bg-[#252526] border border-[#333] rounded-lg p-5 hover:border-[#555] transition-colors group"
              >
                {/* Name + status */}
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0">
                    <h3 className="font-medium text-white truncate">
                      {ws.name}
                    </h3>
                    <p className="text-xs text-[#858585] mt-0.5">
                      {ws.template}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 ml-3">
                    <Circle className={cn('w-2.5 h-2.5', statusColors[ws.status])} />
                    <span className="text-xs text-[#858585] capitalize">
                      {ws.status}
                    </span>
                  </div>
                </div>

                {/* Meta */}
                <p className="text-xs text-[#666] mb-4">
                  Last active:{' '}
                  {new Date(ws.lastActive).toLocaleDateString()}
                </p>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {ws.status === 'running' ? (
                    <>
                      <button
                        onClick={() => handleOpen(ws)}
                        className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#0e639c] hover:bg-[#1177bb] text-white rounded text-xs transition-colors"
                      >
                        <FolderOpen className="w-3.5 h-3.5" />
                        Open
                      </button>
                      <button
                        onClick={() => stopWorkspace(ws.id)}
                        className="inline-flex items-center justify-center p-1.5 text-[#858585] hover:text-yellow-400 hover:bg-[#333] rounded transition-colors"
                        title="Stop"
                      >
                        <Square className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : ws.status === 'stopped' ? (
                    <button
                      onClick={() => startWorkspace(ws.id)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-green-700 hover:bg-green-600 text-white rounded text-xs transition-colors"
                    >
                      <Play className="w-3.5 h-3.5" />
                      Start
                    </button>
                  ) : ws.status === 'creating' ? (
                    <div className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#333] text-[#858585] rounded text-xs">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Creating...
                    </div>
                  ) : (
                    <button
                      onClick={() => startWorkspace(ws.id)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#333] hover:bg-[#444] text-white rounded text-xs transition-colors"
                    >
                      Retry
                    </button>
                  )}

                  {/* Delete */}
                  {deleteConfirm === ws.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDelete(ws.id)}
                        className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs transition-colors"
                      >
                        Confirm
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="px-2 py-1 bg-[#333] hover:bg-[#444] text-white rounded text-xs transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(ws.id)}
                      className="inline-flex items-center justify-center p-1.5 text-[#858585] hover:text-red-400 hover:bg-[#333] rounded transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
